/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useMemo } from 'react';
import { useLogger } from '@shared-ui/logger-context';
import { css, ThemeContext } from 'styled-components';
import { useEgClickstream } from '@shared-ui/clickstream-analytics-context';
import { AnalyticsStore } from 'bernie-plugin-mobx';

//hooks
import { useStores } from 'src/utils/useStore';

//helpers
import {
  checkValue,
  LightenDarkenColor,
  getCustomScript,
  appendMcicidToCmsTypo,
  ItemKeyHelper,
  getDefaultValue,
} from 'src/components/common/helper';
import { titleWeight } from 'src/components/common/theme/component-styles';
//components
import { FootballPageProps } from 'src/components/spotlight/module-util';
import { GlobalNavigationLineOfBusiness } from 'src/common/__generated__/api/types';
import { useHashFragment } from 'src/components/common/modules/hooks/hash-fragment/useHashFragment';
import { MadMarquee } from 'src/components/common/modules/mad-marque/mad-marque';
import { config } from 'config/config';
import Layout from 'src/components/common/theme/Layout';
import { ModuleLoader } from 'src/components/common/modules/meso-modules';
import ModuleStyles from 'src/components/football/pages/module-styles';
import gaLogger from 'src/utils/ga-logger';
import {
  GlobalStyleLoader,
  PartnerHomeStaticStyles,
  StaticCpLiteStyles,
} from 'src/components/common/modules/meso-styles';
import AOS from 'aos';
import { useImageCacheReload } from 'src/components/common/modules/hooks/useImageCacheReload';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FootBallHome: React.FC<FootballPageProps> = (props) => {
  useEffect(() => {
    AOS.init();
    setTimeout(() => {
      AOS.refresh();
    }, 500);
  }, []);
  //accessing the store
  const {
    context: { currency, locale, langId, site },
    //bexapistore,
    cpcmslitefeed: { cpCmsLitePageData, pageModuleVersion },
    analytics,
  } = useStores();

  const analyticsStore: AnalyticsStore = analytics;
  const pageName = `page.${props?.partner}.${props?.templateName}${
    props?.feedName !== '' ? '.' + props?.feedName : ''
  }`;
  const track = useEgClickstream();

  const themeContext: any = useContext(ThemeContext);
  const {
    styles: { cpds },
  } = themeContext;

  // This cpdsTheme Value need to be empty if the cpds is not available
  // Mostly v1 and v2 pages are not having cpds
  // Only v3 pages are having cpds
  const cpdsTheme =
    pageModuleVersion?.toLowerCase() === 'v3' ? `${cpds?.themeType}-${site?.brand}-${cpds?.themeColorMode}` : ``;

  //page data mapping
  const {
    primaryColour,
    secondaryColour,
    bookNowCtaColor,
    bookNowCtaCopyColor,
    cTACopyColor,
    cTACopyWeight,
    cTACopySize,
    cTAStyle,
    heading2CopySizeDesktop,
    heading2CopySizeMobile,
    heading2CopyWeight,
    heading2CopyColor,
    moduleDividerColor,
    footerBackgroundColor,
    footerTextColor,
    content,
    customScript,
    showT2DModule,
    bodyFontColor,
    bodyFont,
    articleStyle,
    moduleHeadingSizeAdjust,

    templateCampaignName,
  } = cpCmsLitePageData;

  useEffect(() => {
    //Initialize GA Tracking
    gaLogger.initializeGa();
    const partner = checkValue(props?.partner) ? props?.partner : 'sports';
    gaLogger.logTraffic(site?.id, props?.feedName, partner, props?.templateName);
    if (checkValue(cpCmsLitePageData) && checkValue(customScript)) {
      getCustomScript(customScript);
    }

    const homeTrackPayload = {
      event: {
        event_type: 'Page View',
        event_category: 'Landing',
        event_name: 'studio_landing.viewed',

        event_version: '1.0.0',
        action_location: 'home_landing',
        event_producer: 'meso-cp-dev',
      },
      experience: {
        page_name: 'Studio landing',
        app_name: 'app-meso-cp-microsites-3',
        app_version: '1.0.0',
        page_name_detailed: `studio${checkValue(props?.templateName) ? '.' + props?.templateName : ''}${
          checkValue(props?.feedName) ? '.' + props?.feedName : ''
        }`,
      },
    };

    track(homeTrackPayload);

    const analyticsContent = {
      pageName: `${pageName}`,
      activityId: `${pageName}-activity`,
      channel: 'bernie',
      events: 'event400',
      prop2: `${pageName}`,
      eVar2: GlobalNavigationLineOfBusiness.UNKNOWN,
      campaign: `${pageName}`,
    };

    const adobeMappedData = {
      adobeMappedData: { ...analyticsContent },
      context: {},
      tealiumUtagData: {},
      version: '1.0.0',
      applicationName: 'app-meso-cp-microsites-3',
    };

    //this functions usually implemented on controller
    //.But when implemented it on controller there have trown two analytics server responses
    //root cause: when rendering components through the controller, it renders twice.(this might be the root cause. not 100% sure)
    //hence implemented this methods in both home page and subpage.
    //This need to be investigated further. here is the temporary solution.
    analyticsStore.resetAnalyticsForNewPage();
    analyticsStore.publishClientSidePayload([adobeMappedData]);

    // Append Mcicid to cms typo
    appendMcicidToCmsTypo(cpCmsLitePageData?.templateType, cpCmsLitePageData?.mainUrl, cpCmsLitePageData?.referrerId);

    const htmlElement = document?.querySelector('html');
    if (htmlElement) {
      htmlElement.className = 'smooth-scrolling';
    }
  }, []);

  // This Will Fire If Url Has Hash Tag
  useHashFragment(0, true);

  // Add query param (v=timeuptohour) to svg urls due to caching issue
  useImageCacheReload();

  const { info } = useLogger();

  info('Rendering Football Home page Content');

  const windowWidth = window?.innerWidth;

  const defaultSetting = {
    style: {
      primaryColour: checkValue(primaryColour) ? primaryColour : '',
      secondaryColour: checkValue(secondaryColour) ? secondaryColour : '',
      darkenColor: checkValue(primaryColour) ? LightenDarkenColor(primaryColour, -0.1) : '',

      cTACopyColor: checkValue(cTACopyColor) ? cTACopyColor : '',
      cTACopyWeight: titleWeight(checkValue(cTACopyWeight) ? cTACopyWeight : 'False'),
      cTACopySize: checkValue(cTACopySize) ? cTACopySize : '',
      cTAStyle: checkValue(cTAStyle) ? cTAStyle : 'primary',

      bookNowCTAColor: checkValue(bookNowCtaColor) ? bookNowCtaColor : '',
      bookNowCTACopyColor: checkValue(bookNowCtaCopyColor) ? bookNowCtaCopyColor : '',

      heading2CopySizeDesktop: checkValue(heading2CopySizeDesktop) ? heading2CopySizeDesktop : '',
      heading2CopySizeMobile: checkValue(heading2CopySizeMobile) ? heading2CopySizeMobile : '',
      heading2CopyWeight: titleWeight(checkValue(heading2CopyWeight) ? heading2CopyWeight : 'False'),
      heading2CopyColor: checkValue(heading2CopyColor) ? heading2CopyColor : '',

      brandColors: { heading: '#141D38', subHeading: '#000000' },
      moduleDividerColor: checkValue(moduleDividerColor) ? moduleDividerColor : '',
      footerBackgroundColor: checkValue(footerBackgroundColor) ? footerBackgroundColor : '',
      footerTextColor: checkValue(footerTextColor) ? footerTextColor : '',
      bodyFontColor: checkValue(bodyFontColor) ? bodyFontColor : '',
      moduleHeadingFontStyle: checkValue(moduleHeadingSizeAdjust)
        ? config.moduleHeadingSizes.find((size) => size.type === moduleHeadingSizeAdjust.toLowerCase())
        : null,
      bodyFont: checkValue(bodyFont) ? bodyFont : '',
    },
    site: {
      locale,
      langId,
      currency,
      brand: site?.brand,
      domain: site?.domain,
      isHotelsCom: site?.brand === 'hotels',
      templateName: checkValue(props?.templateName) ? props.templateName : 'accelerate',
      id: site?.id,
      templateType: cpCmsLitePageData?.templateType,
      campaignUrl: cpCmsLitePageData?.mainUrl,
      partner: props?.partner,
    },
    deviceSize: windowWidth > 1024 ? 'desktop' : windowWidth > 768 ? 'tablet' : 'mobile',
  };

  // If search widget available
  // add on top of it, otherwise as last element
  const pageModulesWithText2Download = (modules: any) => {
    if (!checkValue(modules, true)) return [];
    modules.push(getText2downloadModule());
    return modules;
  };

  const getText2downloadModule = () => (
    <React.Fragment key={`fragblock-Text2download`}>
      <MadMarquee />
    </React.Fragment>
  );

  /**
   * Load the module dynamically from the CMS order
   * @param defaultSetting
   * @param content
   * @returns
   */
  const loadModule = useMemo(() => {
    const pageModules = [];
    const isWebflowTemplate = cpCmsLitePageData?.webflowtemplate;
    const moduleItemref = new ItemKeyHelper('meso-module');
    let t2dLoadedAsModule = false;
    if (checkValue(content)) {
      // Filter content and allow only one mobileAppModule
      const contentOneMobileApp = content?.filter((value, index) => {
        const _value = value.module;
        if (_value === 'mobileAppModule') {
          t2dLoadedAsModule = true;
          return (
            index ===
            content.findIndex((obj) => {
              return obj.module === _value;
            })
          );
        } else if (_value === 'bookingWidget' && value.isShowInHero === 'True') {
          return (
            index ===
            content.findIndex((obj) => {
              return obj.module !== _value;
            })
          );
        } else {
          return value;
        }
      });

      if (checkValue(contentOneMobileApp, true)) {
        contentOneMobileApp?.map((data: any, index: number) => {
          const id = `${data.module}_${index}`;
          const moduleProps = {
            defaultSetting,
            data,
            index,
            isWebflow: isWebflowTemplate?.toLocaleLowerCase() !== 'false',
            articleStyle: checkValue(articleStyle) ? articleStyle : '',
            id,
            mccidData: {
              templateType: cpCmsLitePageData?.templateType,
              campaignUrl: cpCmsLitePageData?.mainUrl,
              referrerId: cpCmsLitePageData?.referrerId,
              extraParam: cpCmsLitePageData?.extraTrackingParam,
            },
            ...(data.module === 'cTAPageNav' ? { content: content } : {}),
            ...(data.module === 'imageModuleArticle'
              ? {
                  image: {
                    img: data.imageSelect,
                    photographer: data.imageCopyright,
                    turnOffGradient: data.turnOffGradient && data.turnOffGradient.toLocaleLowerCase() == 'false',
                    caption: data?.caption,
                    imageAltText: getDefaultValue(data?.imageDescription, 'Article Image'),
                    backgroundColor: data?.backgroundColor,
                    backgroundRectangleBarColor: data?.backgroundRectangleBarColor,
                    moduleName: data.module,
                    articleStyle: articleStyle,
                    moduleWidth: data?.moduleWidth,
                    // pageWidth: data.pageWidth,
                    exClass: 'add-spacing',
                    topPadding: data?.topPadding,
                    bottomPadding: data?.bottomPadding,
                    borderBottomLeftColor: data?.borderBottomLeftColor,
                    borderBottomRightColor: data?.borderBottomRightColor,
                    borderTopLeftColor: data?.borderTopLeftColor,
                    borderTopRightColor: data?.borderTopRightColor,
                  },
                  device: 'desktop',
                }
              : {}),
          };

          pageModules.push(
            <React.Fragment key={`fragblock-${data.module}-${moduleItemref.next()}`}>
              {ModuleLoader(data.module, { ...moduleProps })}
            </React.Fragment>,
          );
        });
      }
    }

    // Check Text 2 download active
    if (checkValue(showT2DModule) && showT2DModule?.toLowerCase() === 'true' && !t2dLoadedAsModule) {
      return pageModulesWithText2Download(pageModules);
    } else {
      return pageModules;
    }
  }, [content]);

  /**
   * Modue styles override from the CMS setting
   * @param cpCmsLitePageData
   * @param defaultSetting
   * @param site
   * @returns
   */
  const moduleCss = (cpCmsLitePageData, defaultSetting) => {
    const moduleStyles = new ModuleStyles(cpCmsLitePageData, defaultSetting);
    const allOverrides = moduleStyles?.Overrides();
    return `${allOverrides}`;
  };

  const staticModuleCss = () => {
    let staticsStyles = StaticCpLiteStyles(content, cpds);
    // Special requirement only palm springs we need to overwrite the styles
    if (templateCampaignName === 'homepagePalmSprings') {
      staticsStyles += PartnerHomeStaticStyles();
    }
    return staticsStyles;
  };

  const partnerHeaderSize = cpCmsLitePageData?.partnerHeaderSize;

  return (
    <>
      <GlobalStyleLoader data={staticModuleCss()} />
      <Layout
        className={`football-container ${
          checkValue(partnerHeaderSize) ? `header-logo-size-${partnerHeaderSize.toLowerCase()}` : ''
        }`}
        css={css`
          ${moduleCss(cpCmsLitePageData, defaultSetting)}
        `}
        cpdsTheme={`${cpdsTheme}`}
      >
        {loadModule}
      </Layout>
    </>
  );
};

export default FootBallHome;
